import React from 'react';
import {
    Button,
    Typography,
    Box,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit,
} from '@mui/icons-material';
import styles from './ChecklistSetting.module.scss';
import type { AssignmentType } from './AssignmentGroupForm';

// ─── Exported interface (used across the module) ──────────────────────────────

export interface ChecklistAssignmentGroup {
    id: number;
    groupName: string;
    /** 'security_role' | 'users' — defaults to 'security_role' for legacy data */
    assignmentType: AssignmentType;
    rolesAssignedLabel: string;
    rolesAssignedId: number | null;
    /** User display labels when assignmentType === 'users' */
    userLabels?: string[];
    /** Raw user ids when assignmentType === 'users' */
    userIds?: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CheckListAndToDoGroups = ({
    handleAddAssignmentClick,
    handleDeleteAssignmentClick,
    assignmentGroups,
    handleEditAssignmentClick,
    loading,
    canEdit = false,
    canDelete = false,
    canExecute = false,
}: {
    handleAddAssignmentClick: () => void;
    handleDeleteAssignmentClick: (id: number) => void;
    assignmentGroups: ChecklistAssignmentGroup[];
    handleEditAssignmentClick: (group: ChecklistAssignmentGroup) => void;
    loading: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
}) => {
    return (
        <div className={styles.consistentPanel}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Checklist and To-Do Assignment Groups</Typography>
                <Button
                    className={styles.button}
                    onClick={handleAddAssignmentClick}
                    disabled={!canExecute}
                    title={canExecute ? 'Add assignment group' : 'You do not have permission to add'}
                    style={{
                        cursor: canExecute ? 'pointer' : 'not-allowed',
                        opacity: canExecute ? 1 : 0.6,
                    }}
                >
                    Add
                </Button>
            </Box>
            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Group Name</th>
                            <th>Assignment Type</th>
                            <th>Assigned To</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                </td>
                            </tr>
                        ) : assignmentGroups.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>
                                    No records found.
                                </td>
                            </tr>
                        ) : (
                            assignmentGroups.map((group) => (
                                <tr key={group.id}>
                                    <td>{group.groupName}</td>
                                    <td>
                                        {group.assignmentType === 'users' ? 'Users' : 'Security Role'}
                                    </td>
                                    <td>
                                        {group.assignmentType === 'users' ? (
                                            group.userLabels && group.userLabels.length > 0 ? (
                                                <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '220px' }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        animation: 'marqueeScroll 12s linear infinite',
                                                        paddingLeft: '100%',
                                                    }}>
                                                        {group.userLabels.join(' · ')}
                                                    </div>
                                                    <style>{`@keyframes marqueeScroll { from { transform: translateX(0); } to { transform: translateX(-100%); } }`}</style>
                                                </div>
                                            ) : group.userIds && group.userIds.length > 0 ? (
                                                group.userIds.map((id) => `User ${id}`).join(' · ')
                                            ) : (
                                                '—'
                                            )
                                        ) : (
                                            group.rolesAssignedLabel || '—'
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        <button
                                            className={styles.iconButton}
                                            onClick={() => handleEditAssignmentClick(group)}
                                            title={canEdit ? 'Edit' : 'You do not have permission to edit'}
                                            disabled={!canEdit}
                                            style={{
                                                opacity: canEdit ? 1 : 0.5,
                                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                            }}
                                        >
                                            <Edit className={styles.editIcon} />
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                                        {assignmentGroups.length > 1 ? (
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => handleDeleteAssignmentClick(group.id)}
                                                title={canDelete ? 'Delete' : 'You do not have permission to delete'}
                                                disabled={!canDelete}
                                                style={{
                                                    opacity: canDelete ? 1 : 0.5,
                                                    cursor: canDelete ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <DeleteIcon className={styles.deleteIcon} />
                                            </button>
                                        ) : (
                                            <Tooltip title="Cannot delete the last assignment group">
                                                <button
                                                    className={styles.iconButton}
                                                    disabled
                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                >
                                                    <DeleteIcon className={styles.deleteIcon} />
                                                </button>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
